/**
 * Global filter lock persistence.
 * Use a feature key per page (e.g. 'clients') so filter lock state survives refresh and navigation.
 * Keys are scoped by tenant: careezy-{tenantSlug}-filter-lock-{feature}
 * Cleared on logout via clearAllFilterLocks().
 */

const STORAGE_KEY_PREFIX = 'careezy-';
const STORAGE_KEY_SUFFIX = '-filter-';

const getStorageKeyPrefix = (): string => {
  const tenantSlug = typeof localStorage !== 'undefined' ? localStorage.getItem('tenantSlug') || '' : '';

  return `${STORAGE_KEY_PREFIX}${tenantSlug}${STORAGE_KEY_SUFFIX}`;
};

export interface FilterLockPersistedState<T = Record<string, unknown>> {
  isLocked: boolean;
  query: Partial<T>;
}

export interface FilterLockState {
  isFilterLocked: boolean;
  query: Record<string, unknown>;
}

export const getFilterLockStorageKey = (feature: string): string => {
  return `${getStorageKeyPrefix()}${feature}`;
};

/**
 * Load persisted filter lock state from localStorage and apply to store state.
 * Call when the page is activated (e.g. onActivated).
 * @param queryTarget - Optional. When the store has no single `query` (e.g. clientQuery vs appointmentQuery), pass the object to restore into.
 */
export const loadPersistedFilterLockState = (
  feature: string,
  state: FilterLockState,
  queryFieldKeys: readonly string[],
  queryTarget?: Record<string, unknown>
): void => {
  try {
    const key = getFilterLockStorageKey(feature);
    const raw = localStorage.getItem(key);
    if (!raw) return;

    const parsed = JSON.parse(raw) as FilterLockPersistedState;
    if (!parsed?.isLocked) return;

    const query = queryTarget ?? state.query;
    if (!query) return;

    state.isFilterLocked = true;

    const q = parsed.query || {};
    for (const k of queryFieldKeys) {
      if (Object.prototype.hasOwnProperty.call(q, k)) {
        query[k] = (q as Record<string, unknown>)[k];
      }
    }
    if (Object.prototype.hasOwnProperty.call(query, 'page')) {
      query.page = 1;
    }
  } catch {
    // Invalid or missing storage; ignore
  }
};

/**
 * Persist current filter lock state to localStorage.
 */
export const savePersistedFilterLockState = (
  feature: string,
  isLocked: boolean,
  query: Record<string, unknown>,
  queryFieldKeys: readonly string[]
): void => {
  const key = getFilterLockStorageKey(feature);
  if (!isLocked) {
    localStorage.removeItem(key);

    return;
  }
  const querySnapshot: Record<string, unknown> = {};
  for (const k of queryFieldKeys) {
    if (Object.prototype.hasOwnProperty.call(query, k)) {
      querySnapshot[k] = query[k];
    }
  }

  const payload: FilterLockPersistedState = {
    isLocked: true,
    query: querySnapshot
  };

  localStorage.setItem(key, JSON.stringify(payload));
};

/**
 * Remove all persisted filter lock state (all tenants). Call on logout.
 */
export const clearAllFilterLocks = (): void => {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k?.startsWith(STORAGE_KEY_PREFIX) && k.includes(STORAGE_KEY_SUFFIX)) keysToRemove.push(k);
  }
  keysToRemove.forEach(k => localStorage.removeItem(k));
};
